import { useState, useEffect, useRef } from "react";
import { useMutation } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { ApiError, apiRequest, queryClient } from "@/lib/query-client";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Upload, Loader2, CloudUpload } from "lucide-react";
import { MAX_FILE_SIZE } from "@shared/uploadConfig";

const MAX_FILES = 20;

interface LeadUploadDocDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  leadId: string;
}

export function LeadUploadDocDialog({ open, onOpenChange, leadId }: LeadUploadDocDialogProps) {
  const { toast } = useToast();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setSelectedFiles([]);
      setIsDragging(false);
    }
  }, [open]);

  const handleFileSelect = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const fileArray = Array.from(files);
    if (fileArray.length > MAX_FILES) {
      toast({
        title: "Too many files",
        description: `Please select up to ${MAX_FILES} files. You selected ${fileArray.length}.`,
        variant: "destructive",
      });
      return;
    }
    setSelectedFiles(fileArray);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const uploadBulkMutation = useMutation({
    mutationFn: async () => {
      if (selectedFiles.length === 0) throw new Error("No files selected");

      for (const file of selectedFiles) {
        if (file.size > MAX_FILE_SIZE) {
          throw new Error(
            `File "${file.name}" is ${(file.size / 1024 / 1024).toFixed(1)}MB, which exceeds the ${Math.round(MAX_FILE_SIZE / 1024 / 1024)}MB limit.`
          );
        }
      }

      const formData = new FormData();
      selectedFiles.forEach(file => formData.append("files", file));

      const response = await apiRequest("POST", `/api/leads/${leadId}/documents/bulk`, formData);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: "Upload failed" }));
        throw new Error(errorData.message || "Upload failed");
      }
      return response.json();
    },
    onSuccess: (data) => {
      if (data.errors && data.errors.length > 0) {
        const errorDetails = data.errors
          .map((e: any) => e.fileName ? `${e.fileName}: ${e.error}` : e.error)
          .join("\n");
        if (data.accepted === 0) {
          toast({ title: "Upload Failed", description: `${data.rejected} file(s) failed:\n${errorDetails}`, variant: "destructive" });
        } else {
          toast({ title: "Partial Success", description: `Uploaded ${data.accepted} file(s). ${data.rejected} failed:\n${errorDetails}` });
        }
      } else {
        toast({
          title: "Files Uploaded",
          description: `Successfully uploaded ${data.accepted} document${data.accepted !== 1 ? "s" : ""}.`,
        });
      }
      queryClient.invalidateQueries({ queryKey: [`/api/leads/${leadId}/documents`] });
      handleClose();
    },
    onError: (error: any) => {
      let title = "Upload Failed";
      let description = error.message && error.message.length < 300
        ? error.message
        : "An unexpected error occurred. Please try again.";
      if (error instanceof ApiError) {
        if (error.status === 413) title = "File Too Large";
        else if (error.status === 408 || error.status === 504) title = "Upload Timed Out";
        else if (error.status >= 500) title = "Server Error";
      }
      toast({ title, description, variant: "destructive" });
    },
  });

  const handleClose = () => {
    onOpenChange(false);
    setSelectedFiles([]);
  };

  const totalMB = Math.round(selectedFiles.reduce((acc, f) => acc + f.size, 0) / 1024 / 1024 * 100) / 100;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Upload Documents</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Select File(s)</Label>
            <div
              className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                isDragging ? "border-primary bg-primary/5" : "border-muted-foreground/25 hover:border-primary/50"
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              data-testid="drop-zone"
            >
              <CloudUpload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" aria-hidden="true" />
              <p className="text-sm text-muted-foreground">
                Drag and drop files here, or <span className="text-primary font-medium">browse</span>
              </p>
              <p className="text-xs text-muted-foreground mt-1">Up to {MAX_FILES} files</p>
            </div>
            <Input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => handleFileSelect(e.target.files)}
              data-testid="input-file"
              disabled={uploadBulkMutation.isPending}
            />
            {selectedFiles.length > 0 && (
              <p className="text-sm text-muted-foreground">
                {selectedFiles.length} / {MAX_FILES} file{selectedFiles.length === 1 ? "" : "s"} selected ({totalMB} MB total)
              </p>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-2">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={uploadBulkMutation.isPending}
            data-testid="button-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={() => uploadBulkMutation.mutate()}
            disabled={selectedFiles.length === 0 || uploadBulkMutation.isPending}
            data-testid="button-confirm-upload"
          >
            {uploadBulkMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                Upload {selectedFiles.length > 0 ? `(${selectedFiles.length})` : ""}
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
